import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, ViewChild, forwardRef, input, output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { GooglePlacesAutocompleteService } from '@core/services/google-places-autocomplete/google-places-autocomplete.service';
import { catchError, iif, map, Observable, of, Subject, switchMap } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { parsePlaceAddressDetails, PlaceAddressDetails } from './helpers/place-address-details.helper';

const SUGGESTIONS_DEBOUNCE_MS = 300;

export interface AutocompleteSuggestion {
	prediction: google.maps.places.PlacePrediction;
	text: string;
	addressDetails?: PlaceAddressDetails;
}

@Component({
	selector: 'app-places-autocomplete',
	standalone: true,
	templateUrl: './places-autocomplete.component.html',
	styleUrls: ['./places-autocomplete.component.scss'],
	providers: [
		GooglePlacesAutocompleteService,
		{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => PlacesAutocompleteComponent), multi: true },
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlacesAutocompleteComponent implements ControlValueAccessor, AfterViewInit {
	constructor(private placesService: GooglePlacesAutocompleteService) {}

	@ViewChild('input') private inputRef?: ElementRef<HTMLInputElement>;

	placeholder = input<string>('');
	hasSearchIcon = input<boolean>(false);

	includedPrimaryTypes = input<string[]>([]);
	includedRegionCodes = input<string[]>([]);
	resolveAddressDetails = input<boolean>(false);
	formatSuggestion = input.required<(prediction: google.maps.places.PlacePrediction) => string>();

	valueSelect = output<AutocompleteSuggestion | null>();

	private inputChange$ = new Subject<string>();

	suggestions = toSignal(
		this.inputChange$.pipe(
			debounceTime(SUGGESTIONS_DEBOUNCE_MS),
			distinctUntilChanged(),
			switchMap((query) => iif(() => !!query.trim(), this.getSuggestions(query), of([]))),
		),
		{ initialValue: [] },
	);
	activeIndex = signal(-1);
	value = signal('');
	disabled = signal(false);
	isOpen = signal(false);

	private onChange: (value: string) => void = () => {};
	private onTouched: () => void = () => {};

	writeValue(value: string): void {
		this.value.set(value ?? '');
	}

	registerOnChange(fn: (value: string) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.disabled.set(isDisabled);
	}

	async ngAfterViewInit(): Promise<void> {
		if (!this.inputRef?.nativeElement) {
			return;
		}
		await this.placesService.ensureLibraryLoaded();
	}

	handleFocus(): void {
		this.isOpen.set(true);
	}

	onInputChange(query: string): void {
		this.value.set(query);
		this.onChange(query);

		const trimmed = query?.trim();
		if (!trimmed) {
			this.valueSelect.emit(null);
			this.clearSuggestions();
			this.isOpen.set(false);
			return;
		}

		this.isOpen.set(true);
		this.inputChange$.next(trimmed);
	}

	private getSuggestions(trimmed: string): Observable<AutocompleteSuggestion[]> {
		return this.placesService
			.getSuggestions(trimmed, {
				includedPrimaryTypes: this.includedPrimaryTypes(),
				includedRegionCodes: this.includedRegionCodes(),
			})
			.pipe(
				map((suggestions) =>
					suggestions.map((suggestion) => ({
						prediction: suggestion.placePrediction,
						text: this.formatSuggestion()(suggestion.placePrediction),
					})),
				),
				catchError((err) => {
					console.error(err);
					return of([]);
				}),
			);
	}

	selectSuggestion(suggestion: AutocompleteSuggestion): void {
		const fields = this.resolveAddressDetails() ? ['formattedAddress', 'addressComponents'] : ['formattedAddress'];

		this.value.set(suggestion.text);
		this.onChange(suggestion.text);
		this.onTouched();
		this.clearSuggestions();
		this.isOpen.set(false);
		this.inputRef?.nativeElement?.blur();

		this.placesService.resolvePlace(suggestion.prediction, fields).subscribe({
			next: (place) => {
				const resolvedSuggestion: AutocompleteSuggestion = this.resolveAddressDetails()
					? { ...suggestion, addressDetails: parsePlaceAddressDetails(place) }
					: suggestion;

				this.valueSelect.emit(resolvedSuggestion);
			},
			error: (err) => {
				console.error(err);
				this.valueSelect.emit(suggestion);
			},
		});
	}

	handleKeydown(e: KeyboardEvent): Promise<void> {
		const list = this.suggestions();
		if (!list.length) {
			return;
		}

		switch (e.key) {
			case 'ArrowDown':
				e.preventDefault();
				this.activeIndex.update((i) => Math.min(i + 1, list.length - 1));
				break;
			case 'ArrowUp':
				e.preventDefault();
				this.activeIndex.update((i) => Math.max(i - 1, 0));
				break;
			case 'Enter': {
				e.preventDefault();
				const active = this.activeIndex();
				if (active >= 0) {
					this.selectSuggestion(list[active]);
				}
				break;
			}
			case 'Escape':
				this.isOpen.set(false);
				break;
		}
	}

	handleBlur(): void {
		this.onTouched();
		this.isOpen.set(false);
	}

	private clearSuggestions(): void {
		this.activeIndex.set(-1);
		this.inputChange$.next('');
	}
}
